import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, LogOut, Users, Star, 
  Search, ArrowLeft, FileText, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import PlayerFormModal from '../components/PlayerFormModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - /uploads/* paths are served from backend
const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}/api${imagePath}`;
  }
  return imagePath;
};

const AdminDashboardPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAdmin();
    fetchPlayers();
  }, [navigate]);

  const fetchAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmin(data);
      } else {
        logout();
      }
    } catch (err) {
      logout();
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/players`);
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (err) {
      toast.error('Error al cargar jugadores');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const openModal = (player = null) => {
    setEditingPlayer(player);
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    try {
      const url = editingPlayer 
        ? `${API_URL}/api/players/${editingPlayer.id}`
        : `${API_URL}/api/players`;
      
      const response = await fetch(url, {
        method: editingPlayer ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success(editingPlayer ? 'Jugador actualizado' : 'Jugador creado');
      setShowModal(false);
      setEditingPlayer(null);
      fetchPlayers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (playerId) => {
    if (!window.confirm('¿Estás seguro de eliminar este jugador?')) return;

    try {
      const response = await fetch(`${API_URL}/api/players/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Jugador eliminado');
      fetchPlayers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredPlayers = players.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.club && p.club.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const featuredCount = players.filter(p => p.is_featured).length;
  const withPageCount = players.filter(p => p.has_page).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/60 hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div className="hidden sm:block w-px h-6 bg-white/10" />
              <h1 className="text-xl font-medium text-white">Panel de Administración</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/60 text-sm hidden sm:block">{admin?.name || 'Admin'}</span>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-3 text-white/50 mb-3">
              <Users size={18} />
              <span className="text-sm">Total Jugadores</span>
            </div>
            <p className="text-3xl font-light text-white">{players.length}</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-3 text-white/50 mb-3">
              <Star size={18} />
              <span className="text-sm">Destacados</span>
            </div>
            <p className="text-3xl font-light text-white">{featuredCount}</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
            <div className="flex items-center gap-3 text-white/50 mb-3">
              <FileText size={18} />
              <span className="text-sm">Con Página</span>
            </div>
            <p className="text-3xl font-light text-white">{withPageCount}</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-5 border border-white/10 flex items-center justify-center">
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-5 py-3 bg-white text-black rounded-full font-medium hover:bg-white/90 transition-all"
              data-testid="add-player-btn"
            >
              <Plus size={18} />
              <span>Agregar Jugador</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white/5 rounded-2xl p-4 border border-white/10 mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar jugador..."
              className="w-full pl-12 pr-4 py-3 bg-transparent text-white placeholder-white/30 outline-none"
            />
          </div>
        </div>

        {/* Players Table */}
        <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-white/50 text-sm font-medium">Jugador</th>
                  <th className="text-left px-6 py-4 text-white/50 text-sm font-medium hidden md:table-cell">Posición</th>
                  <th className="text-left px-6 py-4 text-white/50 text-sm font-medium hidden lg:table-cell">Club</th>
                  <th className="text-left px-6 py-4 text-white/50 text-sm font-medium hidden sm:table-cell">País</th>
                  <th className="text-center px-6 py-4 text-white/50 text-sm font-medium">Destacado</th>
                  <th className="text-center px-6 py-4 text-white/50 text-sm font-medium hidden sm:table-cell">Página</th>
                  <th className="text-right px-6 py-4 text-white/50 text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlayers.map((player) => (
                  <tr key={player.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/10">
                          {player.image ? (
                            <img src={resolveImageUrl(player.image)} alt={player.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                              {player.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{player.name}</p>
                          <p className="text-white/40 text-sm md:hidden">{player.position}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white/70 hidden md:table-cell">{player.position}</td>
                    <td className="px-6 py-4 text-white/70 hidden lg:table-cell">{player.club || '-'}</td>
                    <td className="px-6 py-4 text-white/70 hidden sm:table-cell">{player.country}</td>
                    <td className="px-6 py-4 text-center">
                      {player.is_featured && (
                        <Star size={18} className="inline text-yellow-500 fill-yellow-500" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center hidden sm:table-cell">
                      {player.has_page && (
                        <Link 
                          to={`/jugadores/${player.slug}`}
                          target="_blank"
                          className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink size={16} />
                        </Link>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openModal(player)}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                          title="Editar"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(player.id)}
                          className="p-2 text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPlayers.length === 0 && (
              <div className="py-12 text-center text-white/40">
                No se encontraron jugadores
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Player Form Modal */}
      <PlayerFormModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPlayer(null);
        }}
        editingPlayer={editingPlayer}
        onSave={handleSave}
        getToken={getToken}
      />
    </div>
  );
};

export default AdminDashboardPage;
